/**
 * index.tsx — React 18 bootstrap.
 *
 * Order matters:
 *   1. Import global styles (design.css tokens + app.css overlays).
 *   2. Build the redux store (via App).
 *   3. Bind the axios session-sink so interceptors can read the access
 *      token + secretKey + refresh token without a cyclic import back to
 *      the store.
 *   4. Mount the App component (which sets up Provider + RouterProvider).
 */

import React from 'react';
import { createRoot } from 'react-dom/client';

import './assets/styles/index.css';

import App from './App/App';
import { store } from './redux/store';
import { bindSessionSink } from './services/axios';
import { sessionCleared, tokensRotated } from './redux/slices/auth';
import reportWebVitals from './reportWebVitals';

// Wire the axios interceptors to the redux session state. `get` reads from
// the current store, `set` rotates the trio after a successful refresh, and
// `clear` wipes the session (used on refresh-fail / 401).
bindSessionSink({
	get: () => {
		const s = store.getState().auth;
		return {
			accessToken: s.accessToken,
			refreshToken: s.refreshToken,
			secretKey: s.secretKey,
		};
	},
	set: (next) => {
		const s = store.getState().auth;
		store.dispatch(
			tokensRotated({
				accessToken: next.accessToken ?? s.accessToken ?? '',
				refreshToken: next.refreshToken ?? s.refreshToken ?? '',
				secretKey: next.secretKey ?? s.secretKey ?? '',
			}),
		);
	},
	clear: () => {
		store.dispatch(sessionCleared());
	},
});

const container = document.getElementById('root');
if (!container) throw new Error('#root element missing from public/index.html');

createRoot(container).render(
	<React.StrictMode>
		<App />
	</React.StrictMode>,
);

// Disabled in dev — pass a callback in prod to ship metrics to analytics.
reportWebVitals();
